import { clampTimeRangeToDomain } from '../lib/quality-time-range';
import { StreamQualityPerStationDto } from '../stream-quality.types';

/** Millisecond interval [startMs, endMs) for overlap checks; end is exclusive for bin boundaries. */
export type MinimapMsInterval = {
  startMs: number;
  endMs: number;
};

/**
 * Returns true if a closed bad interval [bad.startMs, bad.endMs] intersects a half-open bin
 * [bin.startMs, bin.endMs) (last bin still uses the same rule; its end is the domain end).
 */
export function badIntervalOverlapsHalfOpenBin(
  bad: MinimapMsInterval,
  bin: MinimapMsInterval,
): boolean {
  return bad.startMs < bin.endMs && bad.endMs >= bin.startMs;
}

/**
 * Builds `binCount` equal time slices over the domain. Last bin ends exactly at `domainEndMs`.
 */
export function buildMinimapTimeBins(
  domainStartMs: number,
  domainEndMs: number,
  binCount: number,
): MinimapMsInterval[] {
  if (binCount < 1 || !(domainEndMs > domainStartMs)) {
    return [];
  }
  const span = domainEndMs - domainStartMs;
  const bins: MinimapMsInterval[] = [];
  for (let i = 0; i < binCount; i += 1) {
    const startMs = domainStartMs + (span * i) / binCount;
    const endMs = i === binCount - 1 ? domainEndMs : domainStartMs + (span * (i + 1)) / binCount;
    bins.push({ startMs, endMs });
  }
  return bins;
}

/**
 * Clamps an interval to the report domain. Returns null if empty after clamp.
 */
export function clampMinimapIntervalToDomain(
  startMs: number,
  endMs: number,
  domainStartMs: number,
  domainEndMs: number,
): MinimapMsInterval | null {
  const domainStart = new Date(domainStartMs);
  const domainEnd = new Date(domainEndMs);
  const clamped = clampTimeRangeToDomain(
    new Date(startMs),
    new Date(endMs),
    domainStart,
    domainEnd,
  );
  if (!clamped) {
    return null;
  }
  return { startMs: clamped.start.getTime(), endMs: clamped.end.getTime() };
}

/**
 * Collects normalized bad (issue + gap) intervals for one station, clamped to the report window.
 */
export function collectStationBadIntervalsMs(
  row: StreamQualityPerStationDto | undefined,
  domainStart: Date,
  domainEnd: Date,
): MinimapMsInterval[] {
  const domainStartMs = domainStart.getTime();
  const domainEndMs = domainEnd.getTime();
  const out: MinimapMsInterval[] = [];

  if (row) {
    for (const issue of row.audioQualityIssues) {
      const startAt = new Date(issue.start).getTime();
      const endAt = new Date(issue.end).getTime();
      const clamped = clampMinimapIntervalToDomain(startAt, endAt, domainStartMs, domainEndMs);
      if (clamped) {
        out.push(clamped);
      }
    }

    for (const gap of row.streamGaps) {
      if (!gap.start) {
        continue;
      }
      const startAt = new Date(gap.start).getTime();
      const endAt = gap.end ? new Date(gap.end).getTime() : domainEndMs;
      const clamped = clampMinimapIntervalToDomain(startAt, endAt, domainStartMs, domainEndMs);
      if (clamped) {
        out.push(clamped);
      }
    }
  }

  return out;
}

/**
 * True if the station has any bad interval overlapping the half-open bin.
 */
export function stationHasBadOverlapInBin(
  badIntervals: MinimapMsInterval[],
  bin: MinimapMsInterval,
): boolean {
  for (const interval of badIntervals) {
    if (badIntervalOverlapsHalfOpenBin(interval, bin)) {
      return true;
    }
  }
  return false;
}

/**
 * Default minimap rule: station is healthy in a bin when no bad interval overlaps that bin.
 */
export function defaultStationHealthyInMinimapBin(
  badIntervals: MinimapMsInterval[],
  bin: MinimapMsInterval,
): boolean {
  return !stationHasBadOverlapInBin(badIntervals, bin);
}
